import type { ApolloCache } from '@apollo/client'
import type { ShiftsShiftFragment } from 'generated/resources/graphql'
import groupBy from 'lodash/groupBy'

import {
  removeRefsFromEntityField,
  type ViewRange,
} from 'modules/shift_management/utils/apolloCache/appendRefsToEntityField'

type ShiftsFieldName = 'shifts' | 'shiftsWithoutWorkArea'

const removeShiftRefsFromEntity = ({
  cache,
  entity,
  fieldName,
  shifts,
  view,
}: {
  cache: ApolloCache<unknown>
  entity: { __typename: string; id: string }
  fieldName: ShiftsFieldName
  shifts: ShiftsShiftFragment[]
  view: ViewRange
}) => {
  removeRefsFromEntityField({
    cache,
    entity,
    fieldName,
    ids: new Set(shifts.map((shift) => shift.id)),
    view,
  })
}

const removeDestroyedShiftsCache = ({
  cache,
  view,
  shifts,
}: {
  cache: ApolloCache<unknown>
  view: ViewRange
  shifts: ShiftsShiftFragment[]
}) => {
  if (shifts.length === 0) return

  cache.batch({
    update: (batchedCache) => {
      const shiftsByEmployee = groupBy(shifts, 'employeeId')
      Object.entries(shiftsByEmployee).forEach(([employeeId, employeeShifts]) => {
        removeShiftRefsFromEntity({
          cache: batchedCache,
          entity: { __typename: 'ShiftManagementEmployeesPermission', id: employeeId },
          fieldName: 'shifts',
          shifts: employeeShifts,
          view,
        })
      })

      const shiftsByWorkArea = groupBy(
        shifts.filter((shift) => shift.locationsWorkAreaId),
        'locationsWorkAreaId'
      )
      Object.entries(shiftsByWorkArea).forEach(([workAreaId, workAreaShifts]) => {
        removeShiftRefsFromEntity({
          cache: batchedCache,
          entity: { __typename: 'ShiftManagementWorkArea', id: workAreaId },
          fieldName: 'shifts',
          shifts: workAreaShifts,
          view,
        })
      })

      const shiftsWithoutWorkAreaByLocation = groupBy(
        shifts.filter((shift) => !shift.locationsWorkAreaId && shift.locationId),
        'locationId'
      )
      Object.entries(shiftsWithoutWorkAreaByLocation).forEach(([locationId, locationShifts]) => {
        removeShiftRefsFromEntity({
          cache: batchedCache,
          entity: { __typename: 'LocationsLocation', id: locationId },
          fieldName: 'shiftsWithoutWorkArea',
          shifts: locationShifts,
          view,
        })
      })

      shifts.forEach((shift) => {
        batchedCache.evict({ id: batchedCache.identify(shift) })
      })
    },
  })
}

export { removeDestroyedShiftsCache }
